
import { useCallback, useMemo } from "react";
import { useRoutineData } from "./useRoutineData";
import { Exercise, SetData } from "../types";

interface UseWorkoutDataWithPersistenceProps {
  routineId: string | undefined;
}

// Helper function to get persisted data from localStorage
const getPersistedSetData = (exerciseId: string, setIndex: number, defaultReps: number, defaultWeight: number) => {
  try {
    const storageKey = `clubbers_set_data_${exerciseId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      const parsed = JSON.parse(stored);
      const setData = parsed[setIndex];
      
      if (setData?.userConfirmed) {
        return {
          reps: setData.reps,
          weight: setData.weight,
          isSaved: true
        };
      }
    }
  } catch (error) {
    console.warn(`⚠️ Error loading persisted data for exercise ${exerciseId}:`, error);
  }
  
  return {
    reps: defaultReps,
    weight: defaultWeight,
    isSaved: false
  };
};

export const useWorkoutDataWithPersistence = ({
  routineId
}: UseWorkoutDataWithPersistenceProps) => {
  // Get original data from Supabase
  const { data: routine, isLoading, error } = useRoutineData(routineId);

  // Process exercises with persisted data
  const exercisesWithPersistedData = useMemo(() => {
    if (!routine?.exercises) return [];

    return routine.exercises.map((exercise: Exercise) => {
      // Create enhanced set_data array with persisted values
      const enhancedSetData = Array.from({ length: exercise.sets }, (_, index) => {
        const persistedSet = getPersistedSetData(
          exercise.id,
          index,
          exercise.reps,
          exercise.weight
        );
        
        if (persistedSet.isSaved) {
          console.log(`🔄 Using persisted data for exercise ${exercise.id}, set ${index}:`, persistedSet);
          return {
            reps: persistedSet.reps,
            weight: persistedSet.weight
          };
        }
        
        // Fallback to original set data or defaults
        const originalSetData = exercise.set_data?.[index];
        return originalSetData || {
          reps: exercise.reps,
          weight: exercise.weight
        };
      });

      const hasPersistedData = enhancedSetData.some((_, index) => 
        getPersistedSetData(exercise.id, index, exercise.reps, exercise.weight).isSaved
      );

      console.log(`💪 Enhanced exercise ${exercise.id} with persisted data:`, {
        originalSets: exercise.set_data?.length || 0,
        enhancedSets: enhancedSetData.length,
        hasPersistedData
      });

      return {
        ...exercise,
        set_data: enhancedSetData
      };
    });
  }, [routine?.exercises]);

  // Create enhanced routine with persisted data
  const enhancedRoutine = useMemo(() => {
    if (!routine) return null;

    return {
      ...routine,
      exercises: exercisesWithPersistedData
    };
  }, [routine, exercisesWithPersistedData]);

  return {
    routine: enhancedRoutine,
    isLoading,
    error
  };
};
